import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DragIcon from "@mui/icons-material/DragIndicator";
import LockIcon from "@mui/icons-material/LockOutlined";
import WarningIcon from "@mui/icons-material/WarningAmber";
import { Box, ButtonBase, Switch, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import { Currency } from "../../../i18n/Currency";
import {
  ApportionmentItem,
  isCostsItem,
  itemKey,
} from "../../../shared/apportionmentTypes";
import { percentage } from "../../../shared/percentage";
import { CurrencyTextField } from "../../general/CurrencyTextField";
import { NO_VALUE, TABULAR_NUMERALS } from "../../general/dataAtoms/shared";
import { SegmentedControl } from "../../general/SegmentedControl";
import { TonalBadge } from "../../general/TonalBadge";

export interface ApportionmentRowProps {
  item: ApportionmentItem;
  label: string;
  // said under the head of the opened row, e.g. where a claimed amount is claimed
  subtitle?: string;
  cap: number;
  allocated: number;
  insufficientFunds: boolean;
  uncovered: boolean;
  currency: Currency;
  expanded: boolean;
  availableFunds: number;
  // nothing of the row can be changed: it has no handle and its controls are off
  readOnly?: boolean;
  onToggleEnabled: (enabled: boolean) => void;
  onToggleExpand: () => void;
  onSpecificAmountChange: (amount: number | undefined) => void;
}

/** How far a row stands back that nothing is paid towards. */
const OFF_OPACITY = 0.55;

/**
 * One of the things a settlement amount is paid towards: costs or a claimed
 * amount, with what is paid of it and whether anything is. Opened, it says how
 * much is paid: as much as there is, or an amount of its own.
 */
const ApportionmentRow = ({
  item,
  label,
  subtitle,
  cap,
  allocated,
  insufficientFunds,
  uncovered,
  currency,
  expanded,
  availableFunds,
  readOnly = false,
  onToggleEnabled,
  onToggleExpand,
  onSpecificAmountChange,
}: ApportionmentRowProps) => {
  const { theme } = useTheme();
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.analytics.settlement.offerDialog.apportionment",
  });

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: itemKey(item), disabled: readOnly });

  // nothing is paid towards the row
  const off = !item.enabled || uncovered;
  const locked = off || readOnly;
  const isSpecific = item.specificAmount !== undefined;
  const notCovered = t("notCovered", {
    amount: currency.format((item.specificAmount ?? cap) - availableFunds),
  });

  const facts = [
    { label: t("cap"), value: currency.format(cap) },
    { label: t("availableFunds"), value: currency.format(availableFunds) },
    ...(cap > 0 && allocated < cap && allocated > 0
      ? [{ label: t("covered"), value: percentage.format(allocated / cap) }]
      : []),
  ];

  return (
    <Box
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: "background.paper",
        overflow: "hidden",
      }}
    >
      <Box
        onClick={onToggleExpand}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          pl: readOnly ? 1.5 : 0.75,
          pr: 0.5,
          py: 0.75,
          cursor: "pointer",
          opacity: off ? OFF_OPACITY : 1,
        }}
      >
        {!readOnly && (
          <Box
            {...attributes}
            {...listeners}
            aria-label={t("move", { label })}
            onClick={(event) => event.stopPropagation()}
            sx={{
              flex: "none",
              display: "flex",
              color: "text.secondary",
              cursor: "grab",
            }}
          >
            <DragIcon fontSize="small" />
          </Box>
        )}

        {/* what is paid goes under the label where the two find no room side by side */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            columnGap: 1,
            rowGap: 0.25,
          }}
        >
          {/* a click anywhere on the head opens the row; this is what the keyboard reaches */}
          <ButtonBase
            disableRipple
            aria-expanded={expanded}
            sx={{
              flex: "1 1 auto",
              minWidth: 0,
              display: "block",
              textAlign: "left",
              borderRadius: 1,
              "&.Mui-focusVisible": {
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: 2,
              },
            }}
          >
            <TonalBadge
              tone={isCostsItem(item) ? undefined : theme.palette.primary}
              sx={{
                px: 0.75,
                py: 0,
                borderRadius: 1,
                letterSpacing: ".03em",
                textTransform: "uppercase",
              }}
            >
              {t(isCostsItem(item) ? "kind.costs" : "kind.claimedAmount")}
            </TonalBadge>
            <Typography
              variant="body2"
              component="span"
              sx={{ display: "block", mt: 0.25, fontWeight: 600 }}
            >
              {label}
            </Typography>
          </ButtonBase>
          <Box
            sx={{
              ml: "auto",
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              whiteSpace: "nowrap",
            }}
          >
            {isSpecific && !off && (
              <LockIcon
                titleAccess={t("specificAmount")}
                sx={{ fontSize: 14, color: "text.secondary" }}
              />
            )}
            <Typography
              variant="body2"
              sx={{
                color:
                  insufficientFunds && isSpecific && !off
                    ? theme.palette.data.negative.dark
                    : "text.secondary",
                ...TABULAR_NUMERALS,
              }}
            >
              {off
                ? NO_VALUE
                : t("paidOfCap", {
                    paid: currency.format(allocated),
                    cap: currency.format(cap),
                  })}
            </Typography>
            {insufficientFunds && !off && (
              <WarningIcon
                titleAccess={notCovered}
                fontSize="small"
                sx={{ color: theme.palette.data.negative.main }}
              />
            )}
          </Box>
        </Box>

        <Switch
          checked={item.enabled}
          onChange={(_, checked) => onToggleEnabled(checked)}
          onClick={(event) => event.stopPropagation()}
          size="small"
          disabled={uncovered || readOnly}
          slotProps={{ input: { "aria-label": t("payTowards", { label }) } }}
          sx={{ flex: "none" }}
        />
      </Box>

      {expanded && (
        <Box
          sx={{
            borderTop: 1,
            borderColor: "divider",
            // in line with the label of the row
            pl: readOnly ? 1.5 : 4.25,
            pr: 1.5,
            pt: 1.25,
            pb: 1.5,
            opacity: off ? OFF_OPACITY : 1,
          }}
        >
          {subtitle && (
            <Typography
              variant="caption"
              sx={{ display: "block", mb: 1, color: "text.secondary" }}
            >
              {subtitle}
            </Typography>
          )}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.25,
            }}
          >
            {/* the control cannot be switched off itself: a disabled fieldset keeps its buttons from being pressed */}
            <Box
              component="fieldset"
              disabled={locked}
              sx={{
                minWidth: 0,
                m: 0,
                p: 0,
                border: "none",
                ...(readOnly &&
                  !off && { opacity: theme.palette.action.disabledOpacity }),
              }}
            >
              <SegmentedControl
                value={isSpecific ? "specific" : "max"}
                onChange={(mode) =>
                  onSpecificAmountChange(mode === "specific" ? 0 : undefined)
                }
                ariaLabel={t("mode.label")}
                options={[
                  { value: "max", label: t("mode.max") },
                  { value: "specific", label: t("mode.specific") },
                ]}
              />
            </Box>
            {isSpecific && (
              <CurrencyTextField
                currency={currency}
                ariaLabel={t("mode.specific")}
                value={item.specificAmount}
                onValueChange={onSpecificAmountChange}
                size="small"
                variant="outlined"
                disabled={locked}
                // gives way to stay beside the choice
                sx={{ flex: "1 1 120px", maxWidth: theme.spacing(20) }}
              />
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              columnGap: 2.75,
              rowGap: 0.75,
              mt: 1.25,
            }}
          >
            {facts.map((fact) => (
              <div key={fact.label}>
                <Typography
                  variant="caption"
                  sx={{ display: "block", color: "text.secondary" }}
                >
                  {fact.label}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 700, ...TABULAR_NUMERALS }}
                >
                  {fact.value}
                </Typography>
              </div>
            ))}
          </Box>

          {insufficientFunds && (
            <Typography
              variant="body2"
              sx={{ mt: 1, color: theme.palette.data.negative.dark }}
            >
              {notCovered}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ApportionmentRow;
